import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Maintenance, ManutenzioneSseService } from '../services/web/manutenzione-sse.service';
import { interval, Subscription } from 'rxjs';
import { UtilityService } from '../services/utility/utility.service';

interface FormatoData {
  giorno: string;
  mese: string;
  anno: string;
  ora: string;
}

@Component({
  selector: 'app-banner-manutenzione',
  templateUrl: './banner-manutenzione.component.html',
  styleUrl: './banner-manutenzione.component.scss',
  standalone: false,
})
export class BannerManutenzioneComponent implements OnInit {
  /** Evento emesso quando lo stato di manutenzione cambia */
  @Output() manutenzioneAttiva: EventEmitter<boolean> = new EventEmitter();

  /** Messaggio da mostrare nel banner */
  messaggio: string | null = null;

  formatDataInizio: FormatoData = {
    giorno: '',
    mese: '',
    anno: '',
    ora: '',
  };
  formatDataFine: FormatoData = {
    giorno: '',
    mese: '',
    anno: '',
    ora: '',
  };

  /** Flag per indicare se la manutenzione è attiva */
  isManutenzione: boolean = false;

  constructor(
    private readonly manutenzioneSSE: ManutenzioneSseService,
    private readonly utilityService: UtilityService,
  ) {}

  /**
   * Inizializza il componente sottoscrivendosi allo stato di manutenzione
   */
  ngOnInit(): void {
    this.manutenzioneSSE.getMaintenanceStatus().subscribe({
      next: ({ data, changed }) => {
        if (!data?.maintenance) {
          this.clearBanner();
          return;
        }

        // Se i dati sono nuovi aggiorno il banner e resetto il timer
        if (changed) {
          this._formatDataInizioFine(data);
          this.showBanner(data);
        }
      },
      error: (err: any) => {
        console.error('Errore nello stato manutenzione:', err);
        this.clearBanner();
      },
    });
  }

  /**
   * Mostra il banner della manutenzione
   * @param data Dati della manutenzione
   */
  private showBanner(data: Maintenance): void {
    this.messaggio = 'Manutenzione programmata: ';
    this.isManutenzione = true;
    this.manutenzioneAttiva.emit(true);
    this.showSnackBar(data);
  }

  /**
   * Controlla se il sito è in mautenzione oppure no
   * Controlla per chiave manutenzione e se è finito il periodo di manutenzione
   * @param data Dati della manutenzione
   * @returns true = isManutenzione
   */
  private _isManutenzionieFinita(data: Maintenance): boolean {
    const now = Date.now();
    const end = new Date(data.start_time).getTime();

    const diffSeconds = Math.max(Math.floor((end - now) / 1000), 0);

    if (diffSeconds === 0 || !data.maintenance) {
      return true;
    }
    return false;
  }

  /**
   * Rimuove il banner di manutenzione e ferma il timer.
   */
  private clearBanner(): void {
    this.messaggio = null;
    this.isManutenzione = false;
    this.manutenzioneAttiva.emit(false);
  }

  /**
   * Mostra un snackbar di avviso per informare l'utente della manutenzione.
   * @param maintenanceData Dati della manutenzione
   */
  private showSnackBar(maintenanceData: any) {
    if (this._isManutenzionieFinita(maintenanceData)) {
      return;
    }

    const userMessage = `⚠️ Gentile utente, ti informiamo che è prevista una manutenzione del sistema il giorno ${this.formatDataInizio.giorno} dalle ore ${this.formatDataInizio.ora} alle ore ${this.formatDataFine.ora}. Durante l’intervento alcuni servizi potrebbero non essere disponibili.`;

    this.utilityService.openSnackBar(userMessage, '✖', {
      duration: Infinity,
      panelClass: ['black-snackbar'],
      verticalPosition: 'top',
    });
  }

  private _formatDataInizioFine(maintenanceData: Maintenance) {
    const dateFormatter = new Intl.DateTimeFormat('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      /*       year: 'numeric', */
    });

    const timeFormatter = new Intl.DateTimeFormat('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });

    const monthFormatter = new Intl.DateTimeFormat('it-IT', { month: 'long' });
    const yearFormatter = new Intl.DateTimeFormat('it-IT', { year: 'numeric' });

    const start = new Date(maintenanceData.start_time);
    const end = new Date(maintenanceData.end_time);

    const formatData = (date: Date) => ({
      giorno: dateFormatter.format(date),
      ora: timeFormatter.format(date),
      mese: monthFormatter.format(date),
      anno: yearFormatter.format(date),
    });

    this.formatDataInizio = formatData(start);
    this.formatDataFine = formatData(end);
  }
}
